/**
 * Saving and opening a file WHERE THE PERSON SAYS.
 *
 * `triggerBlobDownload` next door writes to the browser's download folder
 * without asking, which is right for an export somebody will open once and
 * throw away. It is wrong for a file they are meant to keep and hand back
 * later: "Save" that never asks where is the reason a saved column mapping
 * felt like it had gone nowhere.
 *
 * So the real picker where there is one, and the download where there is not.
 * `showSaveFilePicker` is Chromium's; Firefox and Safari have no equivalent
 * and fall back, which is a worse experience rather than a broken one - the
 * file still arrives, in the usual place, under the name we suggest.
 */
import { triggerBlobDownload } from './download.util';

/** A kind of file, for the picker's filter and its "save as type". */
export interface FileKind {
  description: string;
  mime: string;
  /** Including the dot, as the picker wants them. */
  extensions: string[];
}

export type SaveOutcome =
  /** `asked` is false when the browser had no picker and it went to the
   *  downloads folder instead - the caller says something different then. */
  | { done: true; name: string; asked: boolean }
  /** The person closed the picker. Not a failure, and not worth a message. */
  | { done: false };

export async function saveTextFile(
  text: string,
  suggestedName: string,
  kind: FileKind,
): Promise<SaveOutcome> {
  const picker = (window as unknown as PickerWindow).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker.call(window, {
        suggestedName,
        types: [{
          description: kind.description,
          accept: { [kind.mime]: kind.extensions },
        }],
      });
      const writer = await handle.createWritable();
      await writer.write(new Blob([text], { type: kind.mime }));
      await writer.close();
      return { done: true, name: handle.name, asked: true };
    } catch (failure) {
      if (isAbort(failure)) return { done: false };
      // Anything else - a folder we cannot write to, a handle revoked - is
      // still better served by the file arriving somewhere than by nothing.
    }
  }

  triggerBlobDownload(new Blob([text], { type: kind.mime }), suggestedName);
  return { done: true, name: suggestedName, asked: false };
}

/** Null when the person closed the picker without choosing. */
export async function openTextFile(
  kind: FileKind,
): Promise<{ name: string; text: string } | null> {
  const picker = (window as unknown as PickerWindow).showOpenFilePicker;
  if (picker) {
    try {
      const [handle] = await picker.call(window, {
        multiple: false,
        types: [{
          description: kind.description,
          accept: { [kind.mime]: kind.extensions },
        }],
      });
      const file = await handle.getFile();
      return { name: file.name, text: await file.text() };
    } catch (failure) {
      if (isAbort(failure)) return null;
    }
  }

  return openWithInput(kind);
}

/**
 * The fallback, and the reason it listens for `cancel` as well as `change`:
 * a file input that is dismissed fires nothing at all, so a promise waiting
 * only on `change` never settles - and a caller that put the screen into a
 * "busy" state around it would leave it there for good.
 */
function openWithInput(kind: FileKind): Promise<{ name: string; text: string } | null> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = kind.extensions.join(',');
    input.style.display = 'none';
    document.body.appendChild(input);

    const finish = async (file: File | null) => {
      input.remove();
      resolve(file ? { name: file.name, text: await file.text() } : null);
    };

    input.addEventListener('change', () => void finish(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => void finish(null));
    input.click();
  });
}

function isAbort(failure: unknown): boolean {
  return failure instanceof DOMException && failure.name === 'AbortError';
}

// The File System Access API is not in the DOM lib this project builds
// against, and only the two calls used here are declared - a fuller shim
// would be a promise about the rest of an API nothing calls.
interface PickerType {
  description?: string;
  accept: Record<string, string[]>;
}

interface FileWriter {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}

interface PickedFile {
  name: string;
  createWritable(): Promise<FileWriter>;
  getFile(): Promise<File>;
}

interface PickerWindow {
  showSaveFilePicker?: (
    options?: { suggestedName?: string; types?: PickerType[] },
  ) => Promise<PickedFile>;
  showOpenFilePicker?: (
    options?: { multiple?: boolean; types?: PickerType[] },
  ) => Promise<PickedFile[]>;
}
